import { useContext } from 'react';
import _find from 'lodash/find';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _reduce from 'lodash/reduce';
import _keys from 'lodash/keys';
import _values from 'lodash/values';
import { AppContext, UserContext } from 'commons-ui';

import {
	Entity,
	EntityGroup,
	PAGE_NAMES,
	PAGE_RESOURCE_MAP,
	PermissionLevel,
	MiscellaneousPermissionLevel,
	PERMISSION_LEVEL,
	MISCELLANEOUS_PERMISSION_LEVEL,
	RESOURCE_ENTITY_MAP,
	SettingsEntity,
	MiscellaneousEntity
} from 'constant';
import { getCrmPermissionLevel, getSettingsPermissionLevel, getMiscellaneousPermissionLevel } from './helper';

export interface HasPermissionArgument {
	configPageName: string;
	configResourceName?: string;
	requiredPermissionLevel: PermissionLevel | MiscellaneousPermissionLevel;
	appName?: any;
}

const usePermission = () => {
	const { roleConfig, logoutUser } = useContext(UserContext);
	const {
		app: { appName }
	} = useContext(AppContext);

	const hasPermission = ({ configPageName, configResourceName, requiredPermissionLevel }: HasPermissionArgument): boolean => {
		const { role, roleActions } = roleConfig || { role: 'ADMIN', roleActions: {} };
		if (!roleActions) {
			logoutUser();
			return false;
		}

		if (role === 'ADMIN') {
			return true;
		}

		if (!configResourceName) {
			return _reduce(
				_values(PAGE_RESOURCE_MAP[configPageName]),
				(agg: boolean, resource) =>
					agg || hasPermission({ configPageName, configResourceName: resource, requiredPermissionLevel }),
				false
			);
		}

		const resourceEntities = RESOURCE_ENTITY_MAP[configResourceName as string];

		if (configPageName === PAGE_NAMES.settings) {
			const settingsPermissionConfig = _get(roleActions, 'settings');
			return _reduce(
				resourceEntities,
				(agg: boolean, { entity }) => {
					const permissionLevel = getSettingsPermissionLevel({
						settingsPermissionConfig,
						entity: entity as SettingsEntity
					});

					if (permissionLevel === PERMISSION_LEVEL.WRITE || permissionLevel === requiredPermissionLevel) {
						return true;
					}

					return agg;
				},
				false
			);
		}
		if (configPageName === PAGE_NAMES.miscellaneous) {
			const miscellaneousPermissionConfig = _get(roleActions, 'miscellaneous');
			return _reduce(
				resourceEntities,
				(agg: boolean, { entity }) => {
					const permissionLevel = getMiscellaneousPermissionLevel({
						miscellaneousPermissionConfig,
						entity: entity as MiscellaneousEntity
					});

					if (permissionLevel === requiredPermissionLevel) {
						return true;
					}
					if (
						(requiredPermissionLevel === MISCELLANEOUS_PERMISSION_LEVEL.READ &&
							permissionLevel === MISCELLANEOUS_PERMISSION_LEVEL.MAKER) ||
						permissionLevel === MISCELLANEOUS_PERMISSION_LEVEL.CHECKER
					) {
						return true;
					}

					return agg;
				},
				false
			);
		}
		const crmPermissionConfig = _get(roleActions, 'app');
		return _reduce(
			resourceEntities,
			(agg: boolean, { entityGroup, entity }) => {
				const permissionLevel = getCrmPermissionLevel({
					crmPermissionConfig,
					appName,
					entityGroup: entityGroup as EntityGroup,
					entity: entity as Entity
				});
				if (
					permissionLevel === PERMISSION_LEVEL.WRITE ||
					permissionLevel === requiredPermissionLevel ||
					(permissionLevel === PERMISSION_LEVEL.MASKED && requiredPermissionLevel === PERMISSION_LEVEL.READ)
				) {
					return true;
				}

				return agg;
			},
			false
		);
	};

	const getFirstAccessibleEntityURL = (configPageName: string) => {
		const { roleActions } = roleConfig;
		let entities: any;
		if (configPageName === PAGE_NAMES.settings) {
			entities = _get(roleActions, [configPageName]);
		} else if (appName) {
			entities = _get(roleActions, ['appEntity', appName, configPageName]);
		}
		return _find(_keys(entities), (entityName) => !_isEmpty(entities[entityName]));
	};

	const checkEntityGroupEntityPermission = ({
		entityGroup,
		entity,
		requiredPermissionLevel
	}: {
		entityGroup: string;
		entity: string;
		requiredPermissionLevel: PermissionLevel;
	}) => {
		const { role, roleActions } = roleConfig || { role: 'ADMIN', roleActions: {} };

		if (role === 'ADMIN') {
			return true;
		}

		const crmPermissionConfig = _get(roleActions, 'app');
		const permissionLevel = getCrmPermissionLevel({
			crmPermissionConfig,
			appName,
			entityGroup: entityGroup as EntityGroup,
			entity: entity as Entity
		});
		if (
			permissionLevel === PERMISSION_LEVEL.WRITE ||
			permissionLevel === requiredPermissionLevel ||
			(permissionLevel === PERMISSION_LEVEL.MASKED && requiredPermissionLevel === PERMISSION_LEVEL.READ)
		) {
			return true;
		}

		return false;
	};

	return {
		hasPermission,
		getFirstAccessibleEntityURL,
		checkEntityGroupEntityPermission
	};
};

export default usePermission;
